import { pagedFetch as entreePagedFetch } from '../httpHelpers/papiEntree';
import * as submitterHttp from '../httpHelpers/submitter';
import {
  LearnerBusinessId,
  ReplacementFilesComponentLearnersDto,
  Resubmitter,
  SectionBusinessId,
  VersionedComponentBusinessId,
  VersionedLearnerBusinessId,
} from '@cambridgeassessment/lib-ee-commons';
import { ResubmissionStatusDTO } from '@cambridgeassessment/lib-ee-commons/src/domain/resubmission';

export const getEntriesResponse = async (
  businessUnitId: string,
  seriesId: number,
  centreId: string,
  learnerNumber?: string | null,
  assessmentCode?: string | null,
  assessmentVersion?: number,
  componentNumber?: string,
  nextUrl?: string,
): Promise<{ results: ReplacementFilesComponentLearnersDto[]; nextUrl?: string }> => {
  const path = `v1/businessUnit/${businessUnitId}/centres/${centreId.toUpperCase()}/series/${seriesId}/assessments/${assessmentCode}/assessmentVersion/${assessmentVersion}/component/${componentNumber}/resubmittable-entries`;

  const params = [];
  if (learnerNumber) {
    params.push(`learnerNumber=${encodeURIComponent(learnerNumber)}`);
  }

  const url = params.length > 0 ? `${path}?${params.join('&')}` : path;
  return entreePagedFetch(url, nextUrl);
};

export const toggleLearnerResubmission = async (
  query: VersionedLearnerBusinessId,
  resubmitter?: Resubmitter,
  toggleAssociatedLearners = false,
): Promise<LearnerBusinessId[]> => {
  const q = [
    'v1',
    `businessUnit/${query['businessUnitId']}`,
    `centres/${query['centreId'].toUpperCase()}`,
    `series/${query['seriesId']}`,
    `assessments/${query['assessmentCode']}`,
    `assessmentVersion/${query['assessmentVersion']}`,
    `components/${query['componentNumber']}`,
    `learners/${query['learnerUniqueId']}`,
    'toggle-resubmission',
  ].join('/');

  return submitterHttp.patch(q, { resubmitter, toggleAssociatedLearners });
};

export const getAssociatedLearners = async (
  query: VersionedLearnerBusinessId,
): Promise<VersionedLearnerBusinessId[]> => {
  const q = [
    'v1',
    `businessUnit/${query['businessUnitId']}`,
    `centres/${query['centreId'].toUpperCase()}`,
    `series/${query['seriesId']}`,
    `assessments/${query['assessmentCode']}`,
    `assessmentVersion/${query['assessmentVersion']}`,
    `components/${query['componentNumber']}`,
    `learnerUniqueId/${query['learnerUniqueId']}`,
    'associated-learners',
  ].join('/');

  return submitterHttp.get(q);
};

export const toggleComponentDocumentResubmission = async (
  query: SectionBusinessId,
  resubmitter?: Resubmitter,
): Promise<LearnerBusinessId[]> => {
  const q = [
    'v1',
    `businessUnit/${query['businessUnitId']}`,
    `centres/${query['centreId'].toUpperCase()}`,
    `series/${query['seriesId']}`,
    `assessments/${query['assessmentCode']}`,
    `components/${query['componentNumber']}`,
    `sections/${query['sectionId']}`,
    'toggle-section-resubmission',
  ].join('/');

  return submitterHttp.patch(q, { resubmitter });
};

export const toggleComponentResubmission = async (
  componentBid: VersionedComponentBusinessId,
  resubmitter?: Resubmitter,
): Promise<LearnerBusinessId[]> => {
  const query = [
    'v1',
    `businessUnit/${componentBid['businessUnitId']}`,
    `centres/${componentBid['centreId'].toUpperCase()}`,
    `series/${componentBid['seriesId']}`,
    `assessments/${componentBid['assessmentCode']}`,
    `version/${componentBid['assessmentVersion']}`,
    `components/${componentBid['componentNumber']}`,
    'toggle-component-resubmission',
  ].join('/');

  return submitterHttp.post(query, { resubmitter });
};

export const getComponentResubmissionStatus = async (
  componentBid: VersionedComponentBusinessId,
): Promise<ResubmissionStatusDTO> => {
  const q = [
    'v1',
    `businessUnit/${componentBid['businessUnitId']}`,
    `centres/${componentBid['centreId'].toUpperCase()}`,
    `series/${componentBid['seriesId']}`,
    `assessments/${componentBid['assessmentCode']}`,
    `components/${componentBid['componentNumber']}`,
    'resubmission-status',
  ].join('/');

  return submitterHttp.get(q);
};
